import React, { useState, useEffect, useCallback } from 'react';
import * as fflate from 'fflate';
import { config } from '../../config/config';
import { showToast } from '../utils/Toast';

const fmt = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

// ---------- Client-side streaming ZIP (browser → R2 direct, no server bandwidth) ----------
// Files are fetched directly from R2 using presigned URLs, zipped in-browser with fflate,
// and streamed to disk. The server only hands out signed URLs — no file bytes pass through it.
const downloadCloudflareViaPresignedUrls = async ({ onProgress, signal }) => {
  // 1. Get the signed URL list from our API
  const res = await fetch(`${config.apiUrl}/api/backup/cloudflare/urls`, {
    headers: authHeaders(),
    signal,
  });
  if (!res.ok) throw new Error('Failed to fetch presigned URLs');
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to fetch presigned URLs');

  const objects = data.objects || [];
  const total = objects.length;
  if (total === 0) throw new Error('No files found in R2 bucket');

  // 2. Build a streaming ZIP entirely in the browser
  //    fflate.Zip lets us add files one by one without buffering the whole archive in RAM.
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `topsail-cloudflare-backup-${timestamp}.zip`;

  // Add manifest first
  const manifest = JSON.stringify({
    exportedAt: new Date().toISOString(),
    bucket: data.bucket,
    totalObjects: data.totalObjects,
    totalBytes: data.totalBytes,
    objects: objects.map(({ key, size, lastModified, etag }) => ({ key, size, lastModified, etag })),
  }, null, 2);

  // Collect all zip chunks into an array, then trigger download when finalised
  const chunks = [];
  await new Promise((resolve, reject) => {
    const zip = new fflate.Zip((err, chunk, final) => {
      if (err) { reject(err); return; }
      chunks.push(chunk);
      if (final) resolve();
    });

    const run = async () => {
      try {
        // Manifest
        const manifestFile = new fflate.ZipDeflate('_restore_manifest.json', { level: 6 });
        zip.add(manifestFile);
        manifestFile.push(fflate.strToU8(manifest), true);

        // Fetch and add each file directly from R2
        for (let i = 0; i < objects.length; i++) {
          if (signal?.aborted) { zip.end(); return; }
          const obj = objects[i];
          onProgress?.({ done: i, total, currentKey: obj.key });

          const fileRes = await fetch(obj.url, { signal }); // direct to R2, no server involved
          if (!fileRes.ok) {
            console.warn(`Skipping ${obj.key} — fetch failed (${fileRes.status})`);
            continue;
          }
          const buf = await fileRes.arrayBuffer();
          const entry = new fflate.ZipDeflate(obj.key, { level: 0 }); // level 0 = store; images/PDFs already compressed
          zip.add(entry);
          entry.push(new Uint8Array(buf), true);
        }

        onProgress?.({ done: total, total, currentKey: null });
        zip.end();
      } catch (err) {
        reject(err);
      }
    };
    run();
  });

  // 3. Trigger browser download from assembled chunks
  const blob = new Blob(chunks, { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ---------- Component ----------
const BackupsTab = () => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [downloading, setDownloading] = useState({ mongo: false, cloudflare: false });
  const [cfProgress, setCfProgress] = useState(null); // { done, total, currentKey }
  const abortRef = React.useRef(null);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/backup/stats`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStats(data);
      else showToast(data.message || 'Failed to load backup stats', 'error');
    } catch {
      showToast('Failed to load backup stats', 'error');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // MongoDB: streamed through server (small JSON, no issue)
  const downloadMongo = async () => {
    setDownloading((d) => ({ ...d, mongo: true }));
    try {
      const res = await fetch(`${config.apiUrl}/api/backup/mongo`, { headers: authHeaders() });
      if (!res.ok) { showToast('Download failed', 'error'); return; }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `topsail-mongo-backup-${Date.now()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      showToast('MongoDB backup downloaded', 'success');
    } catch {
      showToast('Download failed', 'error');
    } finally {
      setDownloading((d) => ({ ...d, mongo: false }));
    }
  };

  // Cloudflare: presigned URLs → browser fetches directly from R2 → client ZIP
  const downloadCloudflare = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setDownloading((d) => ({ ...d, cloudflare: true }));
    setCfProgress({ done: 0, total: 0, currentKey: null });
    try {
      await downloadCloudflareViaPresignedUrls({
        signal: controller.signal,
        onProgress: setCfProgress,
      });
      showToast('Cloudflare backup downloaded', 'success');
    } catch (err) {
      if (err.name !== 'AbortError') showToast(err.message || 'Download failed', 'error');
    } finally {
      setDownloading((d) => ({ ...d, cloudflare: false }));
      setCfProgress(null);
      abortRef.current = null;
    }
  };

  const cancelCloudflare = () => {
    abortRef.current?.abort();
  };

  const totalMongoRecords = stats?.mongo?.reduce((s, c) => s + c.count, 0) ?? 0;
  const cfPct = cfProgress?.total ? Math.round((cfProgress.done / cfProgress.total) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Backups</h2>
        <p className="text-sm text-gray-500 mt-1">
          Download a full snapshot of your database or uploaded files. Only super admins can access this.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* MongoDB card */}
        <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600 text-xl">🗄️</div>
            <div>
              <div className="font-semibold text-gray-800">MongoDB Collections</div>
              <div className="text-xs text-gray-400">Full database export as JSON</div>
            </div>
          </div>

          {loadingStats ? (
            <div className="text-sm text-gray-400 animate-pulse">Loading stats…</div>
          ) : stats ? (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 max-h-48 overflow-y-auto">
              {stats.mongo.map((col) => (
                <div key={col.name} className="flex justify-between text-xs text-gray-600">
                  <span className="font-mono truncate">{col.name}</span>
                  <span className="font-medium text-gray-800 ml-2 flex-shrink-0">{col.count.toLocaleString()} docs</span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="text-xs text-gray-500 border-t pt-3 mt-auto">
            {stats ? `${stats.mongo.length} collections · ${totalMongoRecords.toLocaleString()} total docs` : '—'}
          </div>

          <button
            onClick={downloadMongo}
            disabled={downloading.mongo}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {downloading.mongo ? <><span className="animate-spin inline-block">⏳</span> Preparing…</> : <>⬇ Download MongoDB Backup</>}
          </button>
        </div>

        {/* Cloudflare card */}
        <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 text-xl">☁️</div>
            <div>
              <div className="font-semibold text-gray-800">Cloudflare R2 Files</div>
              <div className="text-xs text-gray-400">Downloaded directly from R2 — zero server load</div>
            </div>
          </div>

          {loadingStats ? (
            <div className="text-sm text-gray-400 animate-pulse">Loading stats…</div>
          ) : stats ? (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total files</span>
                <span className="font-semibold text-gray-800">{stats.cloudflare.count.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total size</span>
                <span className="font-semibold text-gray-800">{fmt(stats.cloudflare.sizeBytes)}</span>
              </div>
            </div>
          ) : null}

          {/* Progress bar */}
          {cfProgress && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>{cfProgress.done} / {cfProgress.total} files</span>
                <span>{cfPct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-150"
                  style={{ width: `${cfPct}%` }}
                />
              </div>
              {cfProgress.currentKey && (
                <div className="text-xs text-gray-400 truncate">{cfProgress.currentKey}</div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 leading-relaxed mt-auto">
            Your browser fetches files directly from Cloudflare R2 using short-lived signed links and assembles the ZIP locally — the same approach as MEGA. No data passes through the CRM server.
          </p>

          {downloading.cloudflare ? (
            <button
              onClick={cancelCloudflare}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ✕ Cancel Download
            </button>
          ) : (
            <button
              onClick={downloadCloudflare}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ⬇ Download Cloudflare Backup
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
        <span className="text-lg flex-shrink-0">ℹ️</span>
        <div>
          <strong>Tip:</strong> Run backups regularly and store them off-site. The MongoDB backup includes all CRM records. The Cloudflare backup includes every uploaded file with its original folder structure so it can be restored directly to R2.
        </div>
      </div>
    </div>
  );
};

export default BackupsTab;
